/**
 * Shared date + blocked-range helpers for the lodging booking flow.
 *
 * Day math is UTC-based to match how availability_blocks are stored: half-open
 * `[starts_at, ends_at)` at UTC midnight, where ends_at is the check-out
 * morning — so the check-out day itself is NOT a blocked night and
 * back-to-back stays are allowed. See functions/src/routes/bookings.ts.
 *
 * Used by AvailabilityCalendar (rendering blocked nights) and by
 * StickyBookingWidget / BookingModal (validating URL-seeded or hand-typed
 * dates against the fetched blocks before auto-quoting — issue #485).
 */

export interface BlockedRange {
  starts_at: string;
  ends_at: string;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Build "YYYY-MM-DD" from year / 0-based month / day-of-month. */
export function ymd(y: number, monthIndex: number, d: number): string {
  return `${y}-${pad(monthIndex + 1)}-${pad(d)}`;
}

/** Add n days to a "YYYY-MM-DD" string (UTC math; n may be negative). */
export function addDays(s: string, n: number): string {
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return ymd(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate());
}

/** Today in the viewer's local timezone as "YYYY-MM-DD". */
export function todayYmd(): string {
  const t = new Date();
  return ymd(t.getFullYear(), t.getMonth(), t.getDate());
}

/**
 * Expand blocked ranges into the set of individual blocked NIGHTS
 * ("YYYY-MM-DD"). The check-out morning is excluded (half-open semantics).
 */
export function blockedNightSet(blocks: BlockedRange[]): Set<string> {
  const set = new Set<string>();
  for (const b of blocks) {
    const end = (b.ends_at || '').slice(0, 10);
    let day = (b.starts_at || '').slice(0, 10);
    let guard = 0;
    while (day && end && day < end && guard++ < 800) {
      set.add(day);
      day = addDays(day, 1);
    }
  }
  return set;
}

/**
 * True when the half-open stay [checkIn, checkOut) overlaps any blocked
 * range. A stay checking in on another booking's check-out morning (or
 * checking out on its check-in day) does NOT overlap.
 */
export function rangeOverlapsBlocks(
  checkIn: string,
  checkOut: string,
  blocks: BlockedRange[],
): boolean {
  if (!checkIn || !checkOut || checkOut <= checkIn) return false;
  for (const b of blocks) {
    const start = (b.starts_at || '').slice(0, 10);
    const end = (b.ends_at || '').slice(0, 10);
    if (start && end && checkIn < end && start < checkOut) return true;
  }
  return false;
}
